import { useMemo, useState } from 'react'
import { ListFilter, Pencil, Plus, Trash2 } from 'lucide-react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { DataTable } from '@/components/DataTable'
import { EmptyState } from '@/components/EmptyState'
import { Modal } from '@/components/Modal'
import { Toolbar } from '@/components/Toolbar'
import { useToast } from '@/hooks/useToast'
import { useWatchRules, useDeleteRule } from '@/features/inbox/queries'
import type { InboxWatchRule } from '@/features/inbox/types'
import { WatchRuleFormModal } from './WatchRuleFormModal'

export function WatchRulesPanel({ canManage }: { canManage: boolean }) {
  const { toast } = useToast()
  const rulesQuery = useWatchRules()
  const deleteRule = useDeleteRule()
  const [editing, setEditing] = useState<InboxWatchRule | null>(null)
  const [formOpen, setFormOpen] = useState(false)
  const [pendingDelete, setPendingDelete] = useState<InboxWatchRule | null>(null)

  const rules = rulesQuery.data?.rules ?? []

  const onDelete = async () => {
    if (!pendingDelete) return
    try {
      await deleteRule.mutateAsync(pendingDelete.id)
      toast({ title: 'Rule deleted' })
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Delete failed'
      toast({ title: 'Could not delete', description: msg, variant: 'destructive' })
    } finally {
      setPendingDelete(null)
    }
  }

  const columns = useMemo(
    () => [
      {
        id: 'name',
        header: 'Rule',
        accessorFn: (r: InboxWatchRule) => r.name,
        cell: ({ row }: { row: { original: InboxWatchRule } }) => (
          <div className="min-w-0">
            <div className="truncate font-medium">{row.original.name}</div>
            <div className="truncate text-xs text-muted-foreground">
              {[
                row.original.match_from ? `from:${row.original.match_from}` : null,
                row.original.match_subject ? `subject:${row.original.match_subject}` : null,
              ]
                .filter(Boolean)
                .join(' · ') || '-'}
            </div>
          </div>
        ),
      },
      {
        id: 'category',
        header: 'Category',
        accessorFn: (r: InboxWatchRule) => r.category_name || '-',
      },
      {
        id: 'workflow',
        header: 'Workflow',
        accessorFn: (r: InboxWatchRule) => r.workflow_name || '-',
      },
      {
        id: 'active',
        header: 'Active',
        accessorFn: (r: InboxWatchRule) => (r.is_active ? 'Yes' : 'No'),
        cell: ({ row }: { row: { original: InboxWatchRule } }) =>
          row.original.is_active ? (
            <Badge variant="success">Active</Badge>
          ) : (
            <Badge variant="secondary">Off</Badge>
          ),
      },
      {
        id: 'actions',
        header: '',
        cell: ({ row }: { row: { original: InboxWatchRule } }) => {
          if (!canManage) return <span className="text-xs text-muted-foreground">-</span>
          const r = row.original
          return (
            <div className="flex items-center justify-end gap-1">
              <Button
                variant="ghost"
                size="icon"
                className="size-7"
                title="Edit"
                onClick={() => {
                  setEditing(r)
                  setFormOpen(true)
                }}
              >
                <Pencil className="size-4" />
              </Button>
              <Button
                variant="ghost"
                size="icon"
                className="size-7"
                title="Delete"
                onClick={() => setPendingDelete(r)}
              >
                <Trash2 className="size-4 text-destructive" />
              </Button>
            </div>
          )
        },
      },
    ],
    [canManage]
  )

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center justify-between gap-3">
          <span>Watch rules</span>
          {canManage ? (
            <Toolbar align="end">
              <Button
                size="sm"
                onClick={() => {
                  setEditing(null)
                  setFormOpen(true)
                }}
              >
                <Plus className="size-4" />
                New rule
              </Button>
            </Toolbar>
          ) : null}
        </CardTitle>
      </CardHeader>
      <CardContent>
        <DataTable
          columns={columns}
          rows={rules}
          loading={rulesQuery.isLoading}
          empty={
            <EmptyState
              icon={ListFilter}
              title="No watch rules"
              description="Route mail from a sender or with a subject keyword into a category, or trigger a workflow."
            />
          }
        />
      </CardContent>

      <WatchRuleFormModal open={formOpen} onOpenChange={setFormOpen} rule={editing} />

      <Modal.Confirm
        open={pendingDelete !== null}
        onOpenChange={(o) => !o && setPendingDelete(null)}
        title="Delete watch rule?"
        body={
          <p>
            <span className="font-medium">{pendingDelete?.name}</span> will stop routing new
            mail. Already-categorized emails are unchanged.
          </p>
        }
        confirmLabel="Delete"
        confirmVariant="danger"
        onConfirm={onDelete}
        busy={deleteRule.isPending}
      />
    </Card>
  )
}
